import './ui/theme.css';
import { QUICKMATCH_SPEED_ID, speedById, type SizePref, type SpeedId, type TempoPreset } from '@kolony/shared';
import { mountHomeScreen } from './ui/homeScreen';
import { mountPlayPanel, playAgainPlan, selectionSummary, soloBoardOf, type PlayAgainPlan, type PlaySelection, type SoloBoardId } from './ui/playPanel';
import { loadSeaMode } from './game/seaLoader';
import { createChunkRecovery, launchFlow, type LaunchPorts } from './game/chunkRecovery';
import { mountQueueOverlay } from './ui/queueOverlay';
import { keepaliveDelete, runQuickmatch } from './game/quickmatchLoop';
import { mountLadderView } from './ui/ladderView';
import { StatsApi, type ActivityStat } from './api/stats';
import { createAccountFlows } from './ui/accountUi';
import { AuthSession } from './api/session';
import { SocialApi } from './api/social';
import { RatingApi } from './api/rating';
import { createFollowService } from './game/followService';
import { RankService } from './game/rankService';
import { mountPromotionOverlay } from './ui/promotionOverlay';
import { mountFriendsHub } from './ui/friendsHub';
import { mountSocialPanel } from './ui/socialPanel';
import { overlayPanel } from './ui/dom';
import { AdminApi } from './api/admin';
import { campaignTag, newShareId, referrerHost, shareIdFrom, timeBucket } from './api/attribution';
import { webgl2Available } from './render/capability';
import { freshGuest, makeStores } from './storage/storage';
import { onRenderSettingsChange, powerSavers, renderSettingsFromQuery, renderSettings, setRenderSettings, setSaverOverride, type RenderSettings } from './render/renderSettings';
import { parseSaverOverride } from './render/powerSavers';
import { setBoardVibrance } from './render/boardGrade';
import { applyCssSaver } from './ui/offscreenAnimations';
import { mountFeedback } from './ui/feedback';
import { mountSettingsPanel } from './ui/settingsPanel';
import { createAudioService } from './audio/service';
import { bindAudioUnlock } from './audio/unlockGesture';
import { createNotifier } from './ui/notify';
import { touchPreferred } from './input/touch/detect';
import { createTelemetry, doNotTrack } from './api/telemetry';

// @kolony/client entrypoint — H0 boot shell. Lifted from the seed quarry main.ts (REUSE-MAP
// §6: errorToast/fatalScreen + boot-mode URL routing); the game screens land with their
// milestones (H2 board+hotseat, H3 rooms). What works today: home screen, guest identity,
// name chip, magic-link sign-in/account flows, feedback box, deep-link parsing.

// D45 Item 1 (seed): a small red toast for uncaught client errors — playtest reports say the
// actual exception instead of "the screen went dark". Deduped so a per-frame throw can't spam.
const seenErrors = new Set<string>();
/** Bound after the emitter exists; the toast works before boot finishes either way. */
let telemetryEmitError: ((msg: string) => void) | null = null;
// D304: a lazy chunk that will not load (the previous build's, removed by a
// deploy while this tab was open, or one a dropped connection lost) reloads the page ONCE per
// build per tab. game/chunkRecovery.ts holds the decisions. The build is named by this entry
// chunk's own url, whose hash moves whenever a chunk it can import is renamed.
const chunkRecovery = createChunkRecovery({
  build: import.meta.url,
  marks: () => sessionStorage,
  reload: () => {
    telemetryEmitError?.('chunk-reload'); // the pagehide flush carries it out: how often a deploy strands a tab
    window.location.reload();
  },
});
function errorToast(text: string, opts: { repeat?: boolean } = {}): void {
  // A page already reloading onto a fresh build says nothing on its way out: whatever breaks in
  // that window is an artefact of the import Vite was told not to throw (game/chunkRecovery.ts).
  if (chunkRecovery.reloading()) return;
  const key = text.slice(0, 200);
  // `repeat` is for a notice the player can trigger again, such as a launch that failed. Deduped,
  // the second bounce back to the menu would arrive with no reason given at all.
  if (seenErrors.has(key) && opts.repeat !== true) return;
  seenErrors.add(key);
  // D212: the deduped set IS the interesting set — what actually breaks on real devices
  telemetryEmitError?.(key);
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;top:10px;left:50%;transform:translateX(-50%);max-width:80vw;padding:8px 16px;border-radius:8px;' +
    'background:#3a0d0d;color:#ffdede;font:600 13px ui-monospace,monospace;z-index:200;border:1px solid #a33;box-shadow:0 2px 12px rgba(0,0,0,.4)';
  el.textContent = `⚠ ${text}`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}
window.addEventListener('error', (e) => errorToast(`[kolony] ${e.message}`));
window.addEventListener('unhandledrejection', (e) =>
  errorToast(`[kolony] ${String((e as PromiseRejectionEvent).reason ?? 'unhandled rejection')}`),
);
window.addEventListener('vite:preloadError', (e) => chunkRecovery.onPreloadError(e));

/** Full-screen fatal error with a back-to-menu link (seed D45; H3's resume watchdog uses it). */
export function fatalScreen(text: string): void {
  if (chunkRecovery.reloading()) return; // as errorToast: a page being replaced reports nothing
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;' +
    'background:rgba(8,12,6,.96);color:#f2f7ea;font:600 16px ui-monospace,monospace;z-index:210;text-align:center;padding:24px';
  const msg = document.createElement('div');
  msg.textContent = text;
  msg.style.maxWidth = '520px';
  const back = document.createElement('button');
  back.textContent = 'Back to menu';
  back.style.cssText = 'padding:9px 20px;font-size:15px;border:1px solid var(--accent,#ffb224);background:#40350f;color:#ffe9bd;border-radius:6px;cursor:pointer';
  back.onclick = () => window.location.assign(window.location.pathname);
  el.append(msg, back);
  document.body.appendChild(el);
}

// ── guest identity (brief §6.6: instant guest, localStorage) ────────────────
const stores = makeStores(localStorage);
const guestStore = stores.guest(() => freshGuest());

// D186/D187: the render settings. Applied HERE, before any flow can build a stage, because the
// stage snapshots them at build. The URL wins over the stored blob so a phone can be pointed
// at a tier (`?detail=fine&stats=1`) and a bug report can carry its own repro.
const debugStore = stores.debug;
/** The stored blob minus its version stamp — the store carries `v`, the renderer does not. */
function storedDebug(): RenderSettings {
  const { detail, fps, liveBoard, props, stats } = debugStore.load();
  return { detail, fps, liveBoard, props, stats };
}
function applyDebug(next: RenderSettings): void {
  debugStore.save({ v: 2, ...next });
  setRenderSettings(next);
}
// D294: `?savers=` adjusts the tier's power savers (an A/B measuring tool, never stored)
setSaverOverride(parseSaverOverride(window.location.search));
// D309: tune the tiles' and props' vibrance on the live board — `?vibrance=0.6` (0 = as before)
{
  const v = new URLSearchParams(window.location.search).get('vibrance');
  if (v !== null) setBoardVibrance(Number(v));
}
setRenderSettings(renderSettingsFromQuery(window.location.search, storedDebug()));
// the CSS half of the savers follows the tier live — it is one root attribute
applyCssSaver(document, powerSavers().cssOffscreen);
onRenderSettingsChange(({ next }) => applyCssSaver(document, powerSavers(next).cssOffscreen));
const guest = guestStore.load();
guestStore.save(guest); // persist a fresh identity on first visit

const session = new AuthSession(localStorage);

// ── D212 telemetry (KOLONY-Telemetry-Design.md). Fire-and-forget; the Settings toggle
// and the browser's Do Not Track are both read PER EMIT, so switching off applies live —
// "off" means the data never exists, not that it waits in a queue.
const telemetry = createTelemetry({
  identity: () => guestStore.load().guestId,
  // D258: the bearer, so a signed-in player's events are filed under their ACCOUNT — which is
  // what the privacy page says happens, and the only thing account deletion can find. Until
  // D258 no beacon carried one, and every event was filed under the guest id.
  bearer: () => session.getToken(),
  enabled: () => stores.settings.load().shareUsage && !doNotTrack(),
});
telemetryEmitError = (msg) => telemetry.emit('error', { msg });
addEventListener('pagehide', () => void telemetry.flush());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') void telemetry.flush();
});

// ── D215 notifications: your turn + affordable offers, only while the tab is hidden.
// The Settings toggle is read per fire; the browser permission is asked for in Settings
// (the button click is the user gesture the browser demands).
const notifier = createNotifier({
  doc: document,
  enabled: () => stores.settings.load().notifications,
  onClick: () => telemetry.emit('loop', { what: 'notify_clicked' }), // D250/A6: it worked
});

// ── H12 audio ───────────────────────────────────────────────────────────────
// One service for the page (one AudioContext is all a browser gives you), created here and
// passed down — no module-level singleton, so a test builds its own.
const audio = createAudioService({
  store: {
    isMuted: () => stores.settings.load().muted,
    setMuted: (muted) => stores.settings.save({ ...stores.settings.load(), muted }),
  },
});

// §2.6 THE GESTURE. No browser sounds anything before one, and `resume()` has to be reached
// with no await in front of it — so the listeners do nothing but unlock, and every fetch
// happens after. D251: bound to every gesture kind for the page's lifetime, not `once` on a
// `pointerdown` — a touch's pointerdown is no gesture to WebKit, and an interrupted context
// (a call, the lock screen) needs resuming again. The menu bed is asked for where the menu is
// shown (below): the engine holds a bed requested before the first gesture and starts it then.
bindAudioUnlock(document, audio);

// The most-fired cue in the app (250-500 a session), delegated rather than wired into every
// button: one listener, and a control added later is covered for free. Capture phase so a
// handler that stops propagation still clicks.
document.addEventListener(
  'click',
  (e) => {
    const el = e.target as HTMLElement | null;
    if (el?.closest('button, [role="button"], a[href]')) audio.cue('ui-tap');
  },
  true,
);

// ── boot-mode URL routing (quarry pattern): ?create · ?room=CODE · #join=CODE · ?token ──
const params = new URLSearchParams(location.search);
const pendingRoom = params.get('room');
const hotseatParam = params.get('hotseat');

// ── H13: is this a touch session? (coarse primary pointer + real touch points; the
// `?touch=1|0` URL knob forces either way — input/touch/detect.ts, seeded from FAFI).
// Presence of the `touch` opts is what makes the flows mount the break-menu button and
// the one-time gesture hint; its dismissal persists in the settings store.
const isTouch = touchPreferred(params, {
  matchMedia: (q: string) => window.matchMedia(q),
  maxTouchPoints: navigator.maxTouchPoints,
});
const touchOpts = isTouch
  ? {
      touch: {
        hintSeen: () => stores.settings.load().gestureHintSeen,
        markHintSeen: () => stores.settings.save({ ...stores.settings.load(), gestureHintSeen: true }),
      },
    }
  : {};

// The design §4 `hello`: the one passive beacon nearly every question reads — languages
// (the DROPPED explicit dropdown's passive replacement), device class, tier, PWA, and how
// the session arrived (a share link is the growth loop's fingerprint).
telemetry.emit('hello', {
  languages: [...(navigator.languages ?? [navigator.language])].filter(Boolean).slice(0, 3),
  touch: isTouch,
  tier: storedDebug().detail,
  pwa: window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches,
  viewport: window.innerWidth < 700 ? 'narrow' : window.innerWidth < 1400 ? 'mid' : 'wide',
  // an invite's join link carries via=invite (the friends hub writes it), so the growth
  // loop can tell a friend's invite from a shared table link — both are ?room= boots
  entry: pendingRoom !== null ? (params.get('via') === 'invite' ? 'invite' : 'share') : 'direct',
  // D250/A1: WHERE they came from. `entry` above only ever said whether the URL carried a
  // room code, so every Reddit post, search result and shared link that was not a table
  // landed in one bucket called `direct` — the channel question was not imprecise, it did
  // not exist. Host only, campaign tags sanitised: `api/attribution.ts` owns both rules.
  ...(referrerHost(document.referrer, location.hostname) !== null ? { ref: referrerHost(document.referrer, location.hostname) } : {}),
  ...(campaignTag(location.search) !== null ? { camp: campaignTag(location.search) } : {}),
  rank: rankSummaryForHello(),
});

/**
 * D250/A2: a funnel step, stamped with how far into the session it happened.
 *
 * The bucket is the measurement — "did they reach a game in the first minute" — and a raw
 * millisecond timing would be an entropy source for no extra decision. Every first-session
 * step goes through here so the stamp can never be forgotten on one of them.
 */
function funnelStep(step: string, extra: Record<string, unknown> = {}): void {
  telemetry.emit('funnel', { step, t: timeBucket(performance.now()), ...extra });
}
/** The app booted and painted. The top of every first-session funnel. */
funnelStep('landed');

/**
 * D250/A5: a device that cannot draw the board is an acquisition loss, not a preference —
 * such a player emits `hello` and then nothing, which is indistinguishable from somebody
 * who arrived and chose not to play.
 *
 * ASKED ONLY WHERE A BOARD HAS ALREADY FAILED TO BUILD, never at boot and never on a
 * healthy device. The first cut probed once at startup; it created a real GL context and
 * handed it straight back, which is cheap on a desktop and NOT cheap when six clients do it
 * beside six live stages — T-E2E-009 (six browsers on one room) went from 46.6 s to past its
 * 60 s ceiling, reproduced against HEAD in both directions. A browser gives a page very few
 * contexts, and the honest measurement never needed a prophecy: the global `errorToast`
 * already reports every uncaught exception, so all that was missing was a STABLE KEY telling
 * "this device has no WebGL2" apart from every other stage error. That question is only
 * worth asking after something has already gone wrong.
 */
function stageBuildFailed(e: unknown): never {
  if (!webgl2Available()) telemetry.emit('error', { msg: 'no-webgl2' });
  throw e;
}

// D250/A6: the two moments that make an acquired player worth more than one visit.
addEventListener('appinstalled', () => telemetry.emit('loop', { what: 'pwa_installed' }));

/**
 * D250/A3: the share loop, joined at both ends.
 *
 * One opaque id per hosted room, minted here, written into the invite link the lobby
 * offers, and reported by whoever opens it — so "shares per player" and "arrivals per
 * share" become one query instead of two unrelated counts.
 */
const shareId = newShareId();
const arrivedVia = shareIdFrom(location.search);
if (arrivedVia !== null) telemetry.emit('loop', { what: 'share_opened', sid: arrivedVia });
function rankSummaryForHello(): string {
  try {
    const r = stores.rank.load().record;
    return r === null ? 'none' : String(r.ratedGames);
  } catch {
    return 'none';
  }
}

/**
 * §13.8 quickmatch: enqueue, poll until a room forms, then join it by code — the
 * ordinary join path, so everything downstream (lobby, start, resume) is unchanged. The
 * protocol (and D320's presence half: a keepalive leave on pagehide, a re-ask when the
 * queue dropped us) is `game/quickmatchLoop`; what stays here is the screen around it.
 */

/** D288: how long a player the queue placed in a public room waits for its host to start. */
const PUBLIC_LOBBY_TIMEOUT_MS = 120_000;

const leaveQueueOnExit = keepaliveDelete((url, init) => fetch(url, init));

function startQuickmatch(pick: { speed: SpeedId; size: SizePref }, resume?: { startedAt: number }): void {
  // D216: hide the menu for the wait, exactly as startNet and startHotseat do — otherwise the
  // whole menu sits live under the queue scrim and a second Play mounts a second poll loop.
  home.hide();
  const started = resume?.startedAt ?? Date.now();
  const overlay = mountQueueOverlay({
    doc: document,
    onCancel: () => {
      telemetry.emit('funnel', { step: 'quickmatch_abandoned' });
      overlay.dispose();
      run.cancel();
      home.show();
    },
  });
  overlay.set({ kind: 'joining' });
  if (resume === undefined) telemetry.emit('funnel', { step: 'quickmatch_queue', pool: 'find', speed: pick.speed, size: pick.size });
  const run = runQuickmatch(
    {
      api: session.api,
      sendOnExit: leaveQueueOnExit,
      page: window,
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
    },
    { guestId: guestStore.load().guestId, size: pick.size, speed: pick.speed, resume: resume !== undefined },
    {
      waiting: (status) =>
        overlay.set({
          kind: 'waiting',
          found: status.found ?? 1,
          needed: status.needed ?? 4,
          elapsedMs: Date.now() - started,
          widening: status.widening === true,
          backfillInMs: status.backfillInMs ?? null,
        }),
      formed: (status) => {
        const step = status.public === true ? 'quickmatch_placed' : status.backfill === true ? 'quickmatch_backfilled' : 'quickmatch_formed';
        telemetry.emit('funnel', { step, pool: 'find' });
        overlay.dispose();
        // D288: a PUBLIC room is someone else's lobby — a kick, or a host who never starts,
        // puts the player back in this same queue with their wait intact (resume)
        if (status.public === true) {
          startNet(
            { kind: 'join', code: status.room },
            { lobbyTimeoutMs: PUBLIC_LOBBY_TIMEOUT_MS, onReturn: () => startQuickmatch(pick, { startedAt: started }) },
          );
        } else startNet({ kind: 'join', code: status.room });
      },
      // The overlay owns its own nodes now, so an error can no longer wipe the Cancel
      // button out of the DOM and trap the player under a full-screen scrim (the recorded
      // bug this shape exists to make impossible).
      unavailable: () => overlay.set({ kind: 'unavailable' }),
    },
  );
}

/**
 * H15/D203: the pre-game asset gate both flows run. Settings are read at RUN time, not
 * factory time — the tier chosen in the pause menu must be the tier the next game loads.
 */
function boardPreload(title?: string): import('./render/preload').BoardPreload {
  return {
    ...(title !== undefined ? { title } : {}),
    reducedMotion: () => stores.settings.load().reducedMotion,
    run: async (onProgress, board) => {
      const { preloadStageAssets } = await import('./render/preload');
      const rs = renderSettings();
      const report = await preloadStageAssets({ detail: rs.detail, props: rs.props, sea: board?.sea === true, onProgress });
      telemetry.emit('perf', { preloadMs: Math.round(report.ms), assets: report.total, failed: report.failed, tier: rs.detail });
      // D250/A5: the same number said out loud where the errors are read. A board that came
      // up with placeholders standing is a broken first impression, not a slow one — and the
      // loaders collapse a timeout and a 404 into the same null, so ONE honest message.
      if (report.failed > 0) telemetry.emit('error', { msg: `preload-failed (${String(report.failed)}/${String(report.total)})` });
      return report;
    },
  };
}

/**
 * D304: what both launchers share. Each goes through `launchFlow`, so a
 * launch whose chunks will not load, or whose flow throws while starting, puts the player back on
 * a working menu and says why. The launchers had drifted: startHotseat caught a failed import and
 * blamed the connection, while startNet caught nothing and left an empty page with the menu hidden.
 */
const launchPorts: LaunchPorts = {
  doc: document,
  hideMenu: () => home.hide(),
  showMenu: () => {
    home.show(); // D255: show() is for a flow RETURNING to the menu, which a failed launch is
    audio.music('music-menu'); // a no-op unless the flow had already stepped the menu bed aside
  },
  notify: (text) => errorToast(text, { repeat: true }),
  reloading: () => chunkRecovery.reloading(),
};

function startNet(intent: { kind: 'create' } | { kind: 'join'; code: string }, placement?: { lobbyTimeoutMs: number; onReturn(): void }): void {
  const guestNow = guestStore.load();
  const guestBytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) guestBytes[i] = parseInt(guestNow.guestId.slice(i * 2, i * 2 + 2), 16) || 0;
  void launchFlow(
    launchPorts,
    () => Promise.all([import('./game/netFlow'), import('./net/wsTransport'), import('./render/stage')]),
    ([{ startNetFlow }, { WsTransport }, { createStage }], root) => {
      let flow: { dispose(): void } | null = null;
      const wsUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/game`;
      audio.music(null); // the menu bed steps aside; the flow starts the game bed
      flow = startNetFlow(root, {
        // D288: a placed player returns to the queue, never the menu — tear THIS flow down
        // (socket, lobby, url) and hand the wait back to the queue loop
        ...(placement
          ? {
              placement: {
                lobbyTimeoutMs: placement.lobbyTimeoutMs,
                onReturn: () => {
                  flow?.dispose();
                  root.remove();
                  window.history.replaceState(null, '', window.location.pathname);
                  placement.onReturn();
                },
              },
            }
          : {}),
        audio,
        telemetry,
        notify: notifier,
        ...touchOpts,
        makeTransport: () => new WsTransport(wsUrl),
        name: guestNow.name,
        guestId: guestBytes,
        intent,
        authToken: session.getToken(), // §12.4: a signed-in seat is what makes a game rateable
        shareBase: `${location.origin}/`,
        shareId, // D250/A3: the link the lobby offers carries it; the arriving browser reports it back
        onShareCopied: () => telemetry.emit('loop', { what: 'share_created', sid: shareId }),
        // D190: a signed-in player's name is their account's, so the lobby disables the
        // rename rather than letting the server refuse a control that looked available.
        onSettings: () => settingsPanel.open(),
        signedIn: () => session.signedIn,
        // …and a guest's rename is written back to the guest profile, or the next join
        // would send the OLD name in its Hello and quietly undo it.
        onRenamed: (name) => guestStore.save({ ...guestStore.load(), name }),
        resumeStash: {
          load: () => {
            try {
              return JSON.parse(sessionStorage.getItem('kolony-resume') ?? 'null') as { code: string; tokenHex: string } | null;
            } catch {
              return null;
            }
          },
          save: (v) => sessionStorage.setItem('kolony-resume', JSON.stringify(v)),
          clear: () => sessionStorage.removeItem('kolony-resume'),
        },
        // D209: forward the VIEWPOINT too. netFlow's D191 rebuild handler passed it and this
        // destructuring silently dropped it, so a networked player who changed tier from the
        // pause menu was thrown back to the default framing — the exact loss D191 exists to
        // prevent, working in solo and broken online, invisibly to both. D308: forward the REST
        // wholesale — the route grid's hook was the next field this destructuring would have
        // dropped, in the one file no test reaches.
        stageFactory: ({ boardHost, ...stageOpts }) => {
          try {
            return createStage(boardHost, stageOpts);
          } catch (e) {
            return stageBuildFailed(e); // D250/A5: name the failure before it reaches the toast
          }
        },
        boardPreload: boardPreload(),
        follows,
        canFollow: () => session.signedIn,
        onFatal: (reason) => fatalScreen(`Connection lost: ${reason}`),
        // the room's own url, so a reload resumes THIS game and a share links to it
        onRoomCode: (code) => {
          const next = code ? `${window.location.pathname}?room=${encodeURIComponent(code)}` : window.location.pathname;
          window.history.replaceState(null, '', next);
        },
        // D176 dropped the stash here, before the reload. The FLOW does that now (D257) — and
        // only when the stash is the key to the room being left, so walking out of a lobby you
        // merely looked at no longer throws away the key to the running table you left first.
        onLeave: () => window.location.assign(window.location.pathname),
        // H10-S6b: the invite panel rides the lobby; a guest gets the honest sign-in note
        lobbyExtra: (host, roomCode) => mountSocialPanel(host, social, { roomCode, signedIn: session.signedIn }),
      });
    },
  );
}

function startHotseat(seats: number, solo?: TempoPreset, board: SoloBoardId = 'classic'): void {
  // D276: a scenario's rules, footprints and full catalog are their own lazy chunk — fetched
  // beside the flow's, and only for a sea board, so a classic table never downloads the sea
  const load = () => Promise.all([import('./game/hotseatFlow'), import('./render/stage'), board === 'classic' ? Promise.resolve(null) : loadSeaMode()]);
  // D203's second layer, for code, is launchFlow's: a chunk that fails to arrive (the flow, the
  // stage or the sea) must not strand the player on an empty page with the menu hidden
  void launchFlow(launchPorts, load, ([{ startHotseatFlow }, { createStage: createStageForHotseat }, seaMode], root) => {
    const seedParam = params.get('seed');
    // H11 solo: you are seat 0, the other three are bots. Their names are stable per seat
    // so the same cast turns up every game — the cheapest version of a familiar opponent.
    const names = Array.from({ length: seats }, (_, i) => (i === 0 ? guestStore.load().name : (BOT_NAMES[i - 1] ?? `Settler ${i + 1}`)));
    let stop: (() => void) | null = null;
    const teardown = (): void => {
      stop?.();
      root.remove();
    };
    audio.music(null); // the menu bed steps aside; the flow starts the game bed
    stop = startHotseatFlow(root, {
      seats,
      audio,
      telemetry,
      // D250/A5: the same naming of a failed build the networked flow gets. Passing the
      // factory explicitly keeps the flow's own default (`createStage`) as the thing being
      // wrapped rather than replaced.
      stageFactory: (host, stageOpts) => {
        try {
          return createStageForHotseat(host, stageOpts);
        } catch (e) {
          return stageBuildFailed(e);
        }
      },
      notify: notifier,
      ...touchOpts,
      ...(seedParam !== null ? { seed: Number(seedParam) } : {}),
      names,
      onSettings: () => settingsPanel.open(), // D190: Graphics from the pause menu too
      boardPreload: boardPreload(solo ? 'Solo vs bots' : 'Pass & play'),
      ...(solo ? { solo: { seat: 0, tempo: solo } } : {}),
      // H20-S7: a scenario is chosen by its menu id; the flow picks the footprint by table size
      ...(board !== 'classic' ? { map: board } : {}),
      ...(seaMode ? { catalog: seaMode.SEA_CATALOG } : {}),
      // H11: the game-over popover's two ways out — another table on the same terms, or home
      onReplay: () => {
        teardown();
        startHotseat(seats, solo, board);
      },
      onLeave: () => {
        teardown();
        home.show();
        audio.music('music-menu');
      },
    });
  });
}

/** H11: a small standing cast for solo tables (see KOLONY-Bot-Design.md, personas). */
const BOT_NAMES = ['Vera', 'Osric', 'Marta', 'Bex', 'Cato'];

// ── H14: the rank ladder ────────────────────────────────────────────────────
// One service, two sources: the server tells a signed-in account, and a guest carries the
// server's signed record in local storage. The promotion overlay is mounted on the body as
// its own layer — a rank can be awarded while the player is on the menu OR at a table.
const promotion = mountPromotionOverlay(document.body, {
  play: (cue) => audio.cue(cue),
  reducedMotion: () => stores.settings.load().reducedMotion,
});
// D218: hoisted so the rank service and the ladder view share ONE client rather than each
// minting their own around the same session token.
const ratingApi = new RatingApi(session.api);
const adminApi = new AdminApi(session.api); // D248 — one client per session, like the others
const rankService = new RankService({
  api: ratingApi,
  store: stores.rank,
  guestId: () => guestStore.load().guestId,
  signedIn: () => session.signedIn,
});
/**
 * Bank anything outstanding, re-read the ladder, then announce whatever the player has not
 * been shown yet. Called on launch and on every return to the menu — a guest who finished a
 * game while offline is caught up the next time they open the app.
 */
function refreshRank(): void {
  void rankService
    .settle()
    .then(() => {
      const promo = rankService.takePromotion();
      if (promo) {
        telemetry.emit('loop', { what: 'promotion_shown', rank: promo.rank, awarded: promo.awarded });
        promotion.show(promo.rank, promo.awarded);
      }
    })
    .catch(() => undefined); // a rank is never worth an error screen
}

/**
 * D219: the activity line's number, or null. Fetched ONCE at boot and cached — it changes
 * on the scale of days, and a front-door statistic is not worth a poll.
 *
 * Declared HERE, above the panel that reads it: `mountPlayPanel` paints on mount, so a
 * declaration further down the file is a temporal-dead-zone crash on boot — which is
 * exactly what shipped for ten minutes with every suite green, because a jsdom test injects
 * its own `activity` port and never touches this binding.
 */
let activityStat: ActivityStat | null = null;

/**
 * D221: mark this browser as having actually STARTED a game.
 *
 * The landing page's one-tap lane promises to repeat YOUR last game, so it has to tell a
 * returning player from someone whose store merely holds the defaults — which the presence
 * of a stored selection cannot, because the panel writes those on its first paint.
 */
function markPlayed(): void {
  const p = stores.play.load();
  if (!p.played) stores.play.save({ ...p, played: true });
}

/**
 * D216: the Play panel — mounted once for the session, like every other menu overlay, and
 * the single owner of what the front door offers. Its selection is persisted in its own
 * blob so a returning player is re-offered their last game rather than re-interrogated.
 */
const playPanel = mountPlayPanel({
  doc: document,
  read: () => {
    const p = stores.play.load();
    return { opponents: p.opponents, speed: speedById(p.speed).id, size: p.size, soloBoard: soloBoardOf(p.soloBoard) };
  },
  write: (next) => {
    stores.play.save({
      ...stores.play.load(),
      v: 2,
      opponents: next.opponents,
      speed: next.speed,
      size: next.size,
      ...(next.soloBoard !== undefined ? { soloBoard: next.soloBoard } : {}),
    });
    home.refreshPlaySummary();
  },
  signedIn: () => session.signedIn,
  // D288: one queue — humans first, bots fill whatever the queue cannot
  onFind: (p) => (markPlayed(), startQuickmatch({ speed: p.speed, size: p.size })),
  onSolo: (speed, board, seats) => (markPlayed(), startHotseat(seats, speed, board)),
  onPassAndPlay: () => (markPlayed(), startHotseat(3)),
  activity: () => activityStat?.games ?? null,
});

/**
 * D221: the one-tap lane. The DECISION is `playAgainPlan` (pure, in ui/playPanel.ts, where a
 * test can reach it); this is only the wiring that turns its answer into a flow.
 */
function currentSelection(): PlaySelection {
  const p = stores.play.load();
  return { opponents: p.opponents, speed: speedById(p.speed).id, size: p.size, soloBoard: soloBoardOf(p.soloBoard) };
}

function currentPlan(): PlayAgainPlan {
  return playAgainPlan(currentSelection(), stores.play.load().played);
}

function runPlan(plan: PlayAgainPlan): void {
  const p = stores.play.load();
  // D222: record the lane this tap ACTUALLY played, not just that a game happened — a
  // first-timer's "Jump in" must leave the store on humans, or the next visit's "Play again"
  // starts an offline table they never played.
  stores.play.save({ ...p, played: true, opponents: plan.lane === 'solo' ? 'bots' : 'humans' });
  const sel = currentSelection();
  if (plan.lane === 'solo') startHotseat(plan.seats ?? 4, speedById(sel.speed), plan.board ?? 'classic');
  else if (p.played) startQuickmatch({ speed: sel.speed, size: sel.size });
  // the first-timer's offer is spelled out on the button, so it is sent verbatim
  else startQuickmatch({ speed: QUICKMATCH_SPEED_ID, size: '3-4' });
}

const home = mountHomeScreen({
  doc: document,
  playerName: {
    get: () => guestStore.load().name,
    set: (v) => guestStore.save({ ...guestStore.load(), name: v }),
  },
  signedIn: () => session.signedIn,
  onPlay: () => (funnelStep('cta', { which: 'create' }), startNet({ kind: 'create' })),
  // D250/A2: which door was used. Play OPENS the panel and starts nothing (D216), so the
  // tap and the game it may lead to are two different measurements — and the gap between
  // them is where a front door is fixed.
  onOpenPlay: () => (funnelStep('cta', { which: 'play' }), funnelStep('panel_open', { tab: 'find' }), playPanel.open()),
  playSummary: () => selectionSummary(playPanel.selection()),
  onJoinCode: (code) => (funnelStep('cta', { which: 'join' }), startNet({ kind: 'join', code })),
  onPlayAgain: () => (funnelStep('cta', { which: 'again' }), runPlan(currentPlan())), // the plan is read BEFORE markPlayed flips the branch
  playAgainOffer: () => {
    const { title, sub } = currentPlan();
    return { title, sub };
  },
  activity: () => activityStat,
  reducedMotion: () => stores.settings.load().reducedMotion,
  onSignIn: () => {
    funnelStep('cta', { which: session.signedIn ? 'account' : 'signup' });
    return session.signedIn ? flows.openAccount() : flows.openSignIn();
  },
  onFeedback: () => feedback.open('home'),
  onHowTo: () => funnelStep('cta', { which: 'howto' }),
  onScrolledPastFold: () => funnelStep('scrolled'),
  onSettings: () => settingsPanel.open(),
  onFriends: () => openFriendsHub(),
  // D248: the operator's dashboard. `visible` reads the flag the SERVER put on the account
  // (absent for everyone else), so the entry appears the moment /me answers and disappears
  // on sign-out — and the route re-checks the bearer token regardless of what we drew.
  admin: { visible: () => session.account?.admin === true, open: () => openAdmin() },
  audio,
  rank: {
    current: () => rankService.current(),
    subscribe: (fn) => rankService.onChange(fn),
    refresh: () => refreshRank(),
    onOpenLadder: () => (funnelStep('cta', { which: 'ladder' }), openLadder()),
  },
});

// ── H10-S6b: the social layer (follow/follow-back + room invites) ──
const social = new SocialApi(session.api);
// H16/D204: the one follow set the plaques and the end screen read; refreshed per game.
const follows = createFollowService({ api: social, signedIn: () => session.signedIn, onFollow: () => telemetry.emit('loop', { what: 'follow_tap' }) });
// D218: the ladder, built lazily on first open exactly like the friends hub. The board is
// public, so unlike the hub this needs no sign-in door — a guest can look at what they are
// climbing towards, which is rather the point of showing it to them.
let ladderOverlay: ReturnType<typeof overlayPanel> | null = null;
let ladder: ReturnType<typeof mountLadderView> | null = null;
function openLadder(): void {
  if (!ladderOverlay) {
    ladderOverlay = overlayPanel();
    const content = document.createElement('div');
    ladderOverlay.mount(content);
    ladder = mountLadderView(content, { load: () => ratingApi.leaderboard() }, {
      doc: document,
      onClose: () => {
        ladderOverlay!.overlay.style.display = 'none';
      },
    });
  }
  telemetry.emit('loop', { what: 'ladder_open' });
  ladderOverlay.overlay.style.display = 'flex';
  void ladder!.refresh();
}

/**
 * D248: the operator's dashboard, opened from the utility bar and loaded on FIRST OPEN.
 *
 * The import is dynamic for the reason the GLTFLoader's is: this is a screen exactly one
 * account in the world can see, and every player who cannot see it would otherwise pay for
 * its charts in the bundle they download before the menu paints. The overlay is `wide` —
 * it hosts the dashboard's own card rather than wrapping it in the 560px dialogue panel
 * every other overlay uses, because this one is read at a desk.
 */
let adminOverlay: ReturnType<typeof overlayPanel> | null = null;
let adminView: { refresh(): Promise<void> } | null = null;
function openAdmin(): void {
  const shell = (adminOverlay ??= overlayPanel(document, { wide: true }));
  shell.overlay.style.display = 'flex';
  if (adminView) {
    void adminView.refresh();
    return;
  }
  const content = document.createElement('div');
  shell.mount(content);
  void import('./ui/adminDashboard')
    .then(({ mountAdminDashboard }) => {
      adminView = mountAdminDashboard(content, { load: (days) => adminApi.metrics(days) }, {
        doc: document,
        onClose: () => {
          shell.overlay.style.display = 'none';
        },
      });
      return adminView.refresh();
    })
    .catch(() => {
      // A chunk that will not load must not leave an empty scrim over the menu.
      shell.overlay.style.display = 'none';
    });
}

let hubOverlay: ReturnType<typeof overlayPanel> | null = null;
let hub: ReturnType<typeof mountFriendsHub> | null = null;
function openFriendsHub(): void {
  if (!session.signedIn) {
    flows.openSignIn(); // the graph is an account feature — the sign-in door IS the answer
    return;
  }
  if (!hubOverlay) {
    hubOverlay = overlayPanel();
    const content = document.createElement('div');
    hubOverlay.mount(content);
    hub = mountFriendsHub(content, social, {
      doc: document,
      onJoin: (code) => window.location.assign(`${window.location.origin}/?room=${encodeURIComponent(code)}&via=invite`),
      onClose: () => {
        hubOverlay!.overlay.style.display = 'none';
      },
    });
  }
  hubOverlay.overlay.style.display = 'flex';
  void hub!.refresh().then(() => refreshFriendsBadge());
}
function refreshFriendsBadge(): void {
  if (!session.signedIn) {
    home.setFriendsBadge(0);
    return;
  }
  social
    .invites()
    .then((inv) => home.setFriendsBadge(inv.length))
    .catch(() => home.setFriendsBadge(0)); // a badge is never worth an error screen
}

const flows = createAccountFlows(session, {
  onJoinRoom: (code) => startNet({ kind: 'join', code }),
  // D250/A4: the identity this browser was reporting under a moment ago.
  //
  // The beacon overrides a claimed identity with the verified account id, so without this
  // one field a guest's whole trail ENDS at sign-up and a new one begins — no arrival can
  // be followed across its own conversion, which is the value half of every acquisition
  // question. It is the one addition in this batch with a real privacy cost, so it is
  // named on the privacy page and the account-delete teardown scrubs by it as well.
  // (D258: this fires after the verify has set the token, so the event is stamped with the
  // NEW account's bearer and filed under the account, `prev` naming the guest trail — the row
  // deletion reads its aliases from. Until D258 no beacon carried a bearer, so this row sat
  // under the guest id with `prev` naming itself, and linked nothing.)
  //
  // D256: at the SIGN-IN, never on a state change. It used to ride onStateChange, which also
  // fires on every boot of a signed-in browser and every close of the Account panel — so the
  // dashboard's sign-in count counted launches, and a player who switched usage sharing off
  // before signing in (as the privacy page tells them to) was linked anyway on the first
  // launch after switching it back on.
  onSignedIn: () => telemetry.emit('loop', { what: 'signin', prev: guestStore.load().guestId }),
  // D258: the two moments a bearer stops verifying on purpose. Events carry the credential of
  // the moment they were emitted, so a signed-in player's queued events are the ACCOUNT's —
  // LANDED before sign-out revokes the token (the logout waits for the flush: sent first, a
  // beacon was measured arriving second and filed under the guest id), and DROPPED before
  // deletion, or they could land after the teardown and outlive it.
  onSigningOut: () => telemetry.flush(),
  onDeletingAccount: () => telemetry.discard(),
  // A fresh sign-in is the moment to hand the account the rank built as a guest. The claim
  // clears local storage only on success, so a failure simply retries next launch — which is
  // why the claim stays on EVERY state change, boot included, while the sign-in event above
  // does not.
  onStateChange: () => {
    void rankService
      .claimForAccount()
      .then((claimed) => {
        if (claimed) telemetry.emit('loop', { what: 'save_rank_done' });
      })
      .finally(() => {
        // D255: REFRESH, never show. Boot calls this once the account refresh settles, which
        // is AFTER a `?room=` / `#join=` / `?hotseat=` boot has hidden the menu and put up a
        // lobby or a board — and `show()` brought the menu back over it. Every signed-in
        // player who opened an invite link (or reloaded mid-game) saw the menu while the
        // server seated them. A guest has no token, so boot never gets here: that is why the
        // same link worked from a signed-out browser.
        home.refresh();
        refreshRank();
      });
  },
});
const feedback = mountFeedback({ doc: document, api: session.api, playerName: () => guestStore.load().name });
const settingsPanel = mountSettingsPanel({
  doc: document,
  read: () => renderSettingsFromQuery(window.location.search, storedDebug()),
  write: applyDebug,
  // D210: the master mute rides the Settings panel too — same store as the menu bar and
  // the break menu, synced through onMuteChange like every other instance.
  audio: { isMuted: () => audio.isMuted(), setMuted: (m) => audio.setMuted(m), onMuteChange: (h) => audio.onMuteChange(h) },
  // D212: the anonymous-usage toggle, backed by the settings store; DNT is applied at the
  // emitter, so this switch honestly reflects only the player's own choice
  usage: {
    get: () => stores.settings.load().shareUsage,
    set: (v) => stores.settings.save({ ...stores.settings.load(), shareUsage: v }),
  },
  // D215: the notifications row — permission folded in so the panel can be honest about
  // "blocked in the browser" versus merely "off"
  notify: {
    state: () => (!notifier.supported() ? 'unsupported' : notifier.permission() === 'denied' ? 'denied' : notifier.permission() === 'granted' ? 'granted' : 'default'),
    request: async () => {
      const result = await notifier.request();
      // D250/A6: the PERMISSION, not the toggle — the toggle is a preference, this is the
      // capability, and only one of them can make a player come back.
      if (result === 'granted') telemetry.emit('loop', { what: 'notify_granted' });
    },
    get: () => stores.settings.load().notifications,
    set: (v) => stores.settings.save({ ...stores.settings.load(), notifications: v }),
  },
});

home.show();
audio.music('music-menu'); // starts at the first gesture; a URL-booted game below withdraws it first
refreshRank();
// D219: one fetch, at boot. A missing or below-floor number leaves `activityGames` null and
// the Play panel renders no line at all — silence is the correct render of "not enough yet",
// and a failed request must never put "0 games this week" on the front door.
void new StatsApi(session.api)
  .activity()
  .then((a) => {
    activityStat = a;
    home.refreshActivity(); // the band mounts empty and fills in when the fetch lands
  })
  .catch(() => undefined);
flows.boot(); // honours ?token magic links + #join=CODE deep links
refreshFriendsBadge(); // waiting invites greet a signed-in player at the door
// D176: THE BASE URL IS ALWAYS THE MENU. A game lives at `?room=CODE` and nowhere else.
//
// A resume stash used to be enough on its own to pull you back in, and a stash is GLOBAL:
// loading `/` dropped you into whatever table you were last at — including one you had just
// resigned from, which left no way out of it at all. Resume still works, and is now anchored
// to the room's own url: reload `?room=CODE` and the Hello token reclaims the seat
// (T-E2E-003). Reload `/` and you get the front door, which is what a front door is for.
if (pendingRoom) startNet({ kind: 'join', code: pendingRoom });
if (hotseatParam !== null) {
  const n = Math.min(4, Math.max(3, Number(hotseatParam) || 3)); // the classic random board seats 2–4
  // H20-S3b: `?hotseat=4&map=sea:outer-isles` — pass-and-play on a scenario (3 and 4 both ship)
  startHotseat(n, undefined, soloBoardOf(params.get('map') ?? undefined));
}
